import React from "react"
import { useParams, Link } from "react-router-dom"
import { 
  Spin, 
  Card, 
  Row, 
  Col, 
  Button, 
  Tag, 
  Typography, 
  Table, 
  Alert, 
  Divider, 
  Result,
  Space,
  Steps,
  message,
  Tooltip
} from "antd"
import { 
  WhatsAppOutlined, 
  ShopOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  FileDoneOutlined,
  ReloadOutlined,
  CopyOutlined,
  UserOutlined,
  CarOutlined
} from "@ant-design/icons"
import { usePublicOrderTracking } from "../../hooks/useEcommerce"

const { Title, Text, Paragraph } = Typography

interface StatusConfig {
  badge: React.ReactNode
  alertType: "info" | "success" | "warning" | "error"
  alertTitle: string
  alertDescription: string
  stepCurrent: number
  stepStatus: "wait" | "process" | "finish" | "error"
  statusText: string
}

export function PublicOrderTracking() {
  const { storeSlug, trackingToken } = useParams<{ storeSlug: string; trackingToken: string }>()

  const {
    order,
    error,
    isLoading: loading,
    refetch
  } = usePublicOrderTracking(trackingToken, storeSlug)

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px 20px" }}>
        <Spin size="large" tip="Consultando estado del pedido..." />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div style={{ padding: "50px 20px" }}>
        <Result
          status="404"
          title="Pedido no encontrado"
          subTitle={error || "El código de seguimiento no es válido o ha expirado."}
          extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                Reintentar
              </Button>
              <Link to={`/s/${storeSlug}`}>
                <Button type="primary">Ir al catálogo</Button>
              </Link>
            </Space>
          }
        />
      </div>
    )
  }

  const getOrderStatusConfig = (status: string): StatusConfig => {
    const norm = (status || "").toUpperCase()
    switch (norm) {
      case "PENDING":
        return {
          badge: <Tag icon={<ClockCircleOutlined />} color="orange" style={{ fontSize: 13, padding: "3px 10px" }}>Pendiente de Confirmación</Tag>,
          alertType: "info",
          alertTitle: "Pedido Armado Exitosamente",
          alertDescription: "Tu pedido ha sido registrado en el sistema. El establecimiento verificará los datos de contacto y la disponibilidad de los artículos para la emisión de la factura de venta o salida de almacén.",
          stepCurrent: 1,
          stepStatus: "process",
          statusText: "Pendiente de Confirmación"
        }
      case "PENDING_VALIDATION":
        return {
          badge: <Tag icon={<ClockCircleOutlined />} color="gold" style={{ fontSize: 13, padding: "3px 10px" }}>En Verificación por Almacén</Tag>,
          alertType: "info",
          alertTitle: "Pedido Recibido — En Verificación de Existencias",
          alertDescription: "Tu pedido fue registrado con éxito en nuestro sistema. El equipo de almacén se encuentra validando las existencias físicas de cada artículo y tus datos de contacto para emitir la factura y coordinar el despacho o entrega.",
          stepCurrent: 1,
          stepStatus: "process",
          statusText: "En Verificación por Almacén"
        }
      case "APPROVED":
      case "CONFIRMED":
        return {
          badge: <Tag icon={<CheckCircleOutlined />} color="cyan" style={{ fontSize: 13, padding: "3px 10px" }}>Confirmado por Almacén</Tag>,
          alertType: "success",
          alertTitle: "¡Disponibilidad Confirmada!",
          alertDescription: "Los artículos de tu pedido han sido verificados y separados físicamente en el almacén. En breve se emitirá el documento correspondiente para entrega.",
          stepCurrent: 2,
          stepStatus: "process",
          statusText: "Confirmado por Almacén"
        }
      case "INVOICED":
      case "CONVERTED":
        return {
          badge: <Tag icon={<FileDoneOutlined />} color="green" style={{ fontSize: 13, padding: "3px 10px" }}>Facturado / Emitido</Tag>,
          alertType: "success",
          alertTitle: "¡Pedido Aprobado y Facturado!",
          alertDescription: "Tu pedido ha sido validado y facturado con éxito. La mercancía se encuentra lista para despacho a domicilio o para retiro en el almacén según la modalidad elegida.",
          stepCurrent: 2,
          stepStatus: "finish",
          statusText: "Facturado / Emitido"
        }
      case "CANCELLED":
      case "REJECTED":
        return {
          badge: <Tag icon={<CloseCircleOutlined />} color="red" style={{ fontSize: 13, padding: "3px 10px" }}>Rechazado / Cancelado</Tag>,
          alertType: "error",
          alertTitle: "Pedido Cancelado",
          alertDescription: "Este pedido no pudo ser procesado o fue cancelado. Si tienes dudas o deseas realizar un nuevo pedido, por favor comunícate directamente con la tienda.",
          stepCurrent: 1,
          stepStatus: "error",
          statusText: "Rechazado / Cancelado"
        }
      default:
        return {
          badge: <Tag color="default" style={{ fontSize: 13, padding: "3px 10px" }}>{status}</Tag>,
          alertType: "info",
          alertTitle: `Estado del Pedido: ${status}`,
          alertDescription: "Tu pedido está registrado y en proceso de gestión comercial en el almacén.",
          stepCurrent: 1,
          stepStatus: "process",
          statusText: status
        }
    }
  }

  // Normalización de código y claves
  const orderCode = order.order_code || order.order_number || trackingToken || ""
  const statusConfig = getOrderStatusConfig(order.status)

  // Normalización de datos del comprador
  const customer = order.customer_snapshot || order.submitted_customer_data || {}
  const customerName = customer.name && customer.name !== "N/A" ? customer.name : ""
  const customerDocType = customer.document_type || "CC"
  const customerDocNumber = customer.document_number && customer.document_number !== "N/A" ? customer.document_number : ""
  const customerPhone = customer.phone && customer.phone !== "N/A" ? customer.phone : ""
  const customerEmail = customer.email || ""

  // Normalización de entrega
  const delivery = order.delivery_info || {}
  const rawDeliveryModality = order.delivery_modality || delivery.delivery_method || delivery.delivery_modality
  const isDelivery = rawDeliveryModality === "DELIVERY" || Boolean(order.delivery_address || delivery.address)
  const deliveryAddress = order.delivery_address || delivery.address || delivery.delivery_address || ""
  const customerNotes = order.customer_notes || order.notes || delivery.notes || ""

  // Normalización de tienda
  const store = order.ecommerce_stores || {}
  const storeName = store.name || store.store_name || order.store_name || "la tienda"
  const contactPhone = store.contact_whatsapp || store.contact_phone || order.contact_whatsapp || order.contact_phone || ""
  const whatsappNumber = contactPhone ? String(contactPhone).replace(/\D/g, "") : ""

  // Normalización de items
  const rawItems = order.ecommerce_order_items || order.items || []
  const items = rawItems.map((it: any, idx: number) => {
    const name = it.name || it.item_name || "Producto"
    const code = it.code || it.item_sku || it.sku || ""
    const qty = Number(it.quantity) || 1
    const unitPrice = Number(it.unit_price ?? it.price) || 0
    const total = Number(it.total ?? it.total_amount ?? (qty * unitPrice)) || 0

    return {
      key: it.id || it.element_id || idx,
      id: it.id || it.element_id || idx,
      name,
      item_name: name,
      code,
      sku: code,
      quantity: qty,
      unit_price: unitPrice,
      total,
      total_amount: total
    }
  })

  // Normalización de totales
  const subtotal = Number(order.subtotal_amount) || 0
  const tax = Number(order.tax_amount) || 0
  const discount = Number(order.discount_amount) || 0
  const deliveryCost = Number(order.delivery_cost) || 0
  const totalAmount = Number(order.total_amount) || (subtotal + tax + deliveryCost - discount)

  const copyOrderCode = () => {
    if (orderCode) {
      navigator.clipboard?.writeText(orderCode)
      message.success(`Código ${orderCode} copiado al portapapeles`)
    }
  }

  const whatsappMessage = encodeURIComponent(
    `Hola, armé el pedido *${orderCode}* a nombre de *${customerName || "Cliente"}* por valor de $${totalAmount.toLocaleString()} en su tienda online y deseo consultar el estado de entrega.`
  )

  const columns = [
    {
      title: "Producto",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text || record.item_name || "Producto"}</Text>
          {(record.code || record.sku) && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>SKU / Cód: {record.code || record.sku}</Text></div>
          )}
        </div>
      )
    },
    {
      title: "Cant.",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      width: 70
    },
    {
      title: "Precio Unit.",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right" as const,
      render: (val: any) => `$${(Number(val) || 0).toLocaleString()}`
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right" as const,
      render: (val: any) => `$${(Number(val) || 0).toLocaleString()}`
    }
  ]

  const formattedDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : ""

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f7fa", padding: "24px 16px" }}>
      <div style={{ maxWidth: 840, margin: "0 auto" }}>
        {/* Navigation & Actions */}
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Link to={`/s/${storeSlug}`}>
            <Button icon={<ShopOutlined />}>Volver al Catálogo</Button>
          </Link>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Actualizar Estado
            </Button>
            {whatsappNumber && (
              <Button 
                type="primary" 
                icon={<WhatsAppOutlined />} 
                style={{ backgroundColor: "#25D366", borderColor: "#25D366" }}
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
              >
                Contactar por WhatsApp
              </Button>
            )}
          </Space>
        </div>

        {/* Main Order Card */}
        <Card style={{ marginBottom: 20, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <Space align="center" wrap>
                <Title level={3} style={{ margin: 0 }}>Pedido #{orderCode}</Title>
                <Tooltip title="Copiar código del pedido">
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    size="small" 
                    onClick={copyOrderCode}
                  />
                </Tooltip>
              </Space>
              {formattedDate && (
                <div><Text type="secondary" style={{ fontSize: 13 }}>Registrado el: {formattedDate}</Text></div>
              )}
            </div>
            <div>
              {statusConfig.badge}
            </div>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Stepper visual progress */}
          <div style={{ padding: "8px 12px 16px 12px" }}>
            <Steps
              current={statusConfig.stepCurrent}
              status={statusConfig.stepStatus}
              items={[
                {
                  title: "Pedido Armado",
                  description: "Registrado en el sistema"
                },
                {
                  title: "En Verificación",
                  description: statusConfig.stepStatus === "error" ? "Pedido cancelado" : "Validando existencias"
                },
                {
                  title: "Facturado y Listo",
                  description: "Despacho o retiro en almacén"
                }
              ]}
            />
          </div>

          {/* Descriptive Alert Banner */}
          <Alert
            type={statusConfig.alertType}
            showIcon
            message={<Text strong style={{ fontSize: 15 }}>{statusConfig.alertTitle}</Text>}
            description={statusConfig.alertDescription}
            style={{ marginBottom: 16, borderRadius: 6 }}
          />

          {order.client_verification_required && (
            <Alert
              type="warning"
              showIcon
              message="Verificación de Datos Requerida"
              description="Los datos del documento ingresado están sujetos a validación por parte del asesor comercial previo a la emisión y facturación legal."
              style={{ marginBottom: 16, borderRadius: 6 }}
            />
          )}

          {/* Customer & Delivery Data */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12}>
              <Card 
                type="inner" 
                title={<Space><UserOutlined /><span>Datos del Comprador</span></Space>} 
                size="small"
                style={{ height: "100%", borderRadius: 6 }}
              >
                <p style={{ margin: "4px 0" }}><strong>Nombre:</strong> {customerName || "No especificado"}</p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Documento:</strong> {customerDocNumber ? `${customerDocType} ${customerDocNumber}` : "No registrado"}
                </p>
                <p style={{ margin: "4px 0" }}><strong>Teléfono:</strong> {customerPhone || "No registrado"}</p>
                {customerEmail && (
                  <p style={{ margin: "4px 0" }}><strong>Email:</strong> {customerEmail}</p>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card 
                type="inner" 
                title={<Space><CarOutlined /><span>Modalidad de Entrega</span></Space>} 
                size="small"
                style={{ height: "100%", borderRadius: 6 }}
              >
                <p style={{ margin: "4px 0" }}>
                  <strong>Tipo:</strong> {isDelivery ? "Envío a domicilio 🚚" : "Recoger en tienda / Almacén 🏬"}
                </p>
                {deliveryAddress && (
                  <p style={{ margin: "4px 0" }}><strong>Dirección:</strong> {deliveryAddress}</p>
                )}
                <p style={{ margin: "4px 0" }}>
                  <strong>Notas / Instrucciones:</strong> {customerNotes || "Sin instrucciones adicionales"}
                </p>
              </Card>
            </Col>
          </Row>

          {/* Items Table */}
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
              Artículos del Pedido ({items.length})
            </Text>
            <Table 
              columns={columns} 
              dataSource={items} 
              pagination={false} 
              size="small"
              bordered
            />
          </div>

          {/* Totals Summary */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ width: 280, backgroundColor: "#fafafa", padding: 16, borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <Text type="secondary">Subtotal:</Text>
                <Text strong>${subtotal.toLocaleString()}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <Text type="secondary">Impuestos:</Text>
                <Text>${tax.toLocaleString()}</Text>
              </div>
              {discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <Text type="secondary">Descuento:</Text>
                  <Text type="danger">-${discount.toLocaleString()}</Text>
                </div>
              )}
              {deliveryCost > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <Text type="secondary">Costo de Envío:</Text>
                  <Text>${deliveryCost.toLocaleString()}</Text>
                </div>
              )}
              <Divider style={{ margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Title level={4} style={{ margin: 0 }}>Total:</Title>
                <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                  ${totalAmount.toLocaleString()}
                </Title>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default PublicOrderTracking
